import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './VideoStatus.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

const VideoStatus = () => {
  const { jobId } = useParams();
  const [videoJob, setVideoJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let intervalId;

    const fetchVideoStatus = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/customer/videos/${jobId}`
        );

        setVideoJob(response.data);
        setLoading(false);

        // Stop polling if video is complete or failed
        if (response.data.status === 'complete' || response.data.status === 'failed') {
          if (intervalId) {
            clearInterval(intervalId);
          }
        }
      } catch (err) {
        console.error('Error fetching video status:', err);
        setError('Could not load video status');
        setLoading(false);
        if (intervalId) {
          clearInterval(intervalId);
        }
      }
    };

    // Initial fetch
    fetchVideoStatus();

    // Poll every 5 seconds
    intervalId = setInterval(fetchVideoStatus, 5000);

    // Cleanup
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [jobId]);

  if (loading) {
    return (
      <div className="video-status loading">
        <div className="spinner"></div>
        <p>Loading video status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-status error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="video-status">
      <div className="status-content">
        <h1>{videoJob.petName}'s Video</h1>

        {/* Status Indicator */}
        <div className={`status-badge status-${videoJob.status}`}>
          {videoJob.status === 'pending' && '⏳ Pending'}
          {videoJob.status === 'processing' && '⚙️ Processing'}
          {videoJob.status === 'complete' && '✅ Complete'}
          {videoJob.status === 'failed' && '❌ Failed'}
        </div>

        {/* Processing Message */}
        {(videoJob.status === 'pending' || videoJob.status === 'processing') && (
          <div className="status-message">
            <div className="spinner"></div>
            <p>Your video is being created... This usually takes 2-3 minutes.</p>
            <p className="status-detail">We're generating a personalized video with {videoJob.emotion} emotion!</p>
          </div>
        )}

        {/* Complete - Show Video */}
        {videoJob.status === 'complete' && videoJob.videoUrl && (
          <div className="video-container">
            <video controls className="video-player">
              <source src={videoJob.videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>

            {videoJob.transcript && (
              <div className="transcript">
                <h3>Transcript</h3>
                <p>{videoJob.transcript}</p>
              </div>
            )}

            <div className="video-actions">
              <a
                href={videoJob.videoUrl}
                download={`${videoJob.petName}-video.mp4`}
                className="btn-primary"
              >
                Download Video
              </a>
            </div>
          </div>
        )}

        {/* Failed Message */}
        {videoJob.status === 'failed' && (
          <div className="status-message error">
            <p>Sorry, something went wrong creating your video.</p>
            {videoJob.errorMessage && (
              <p className="error-detail">{videoJob.errorMessage}</p>
            )}
            <p>Please try again or contact support.</p>
          </div>
        )}

        {/* Job Details */}
        <div className="job-details">
          <h3>Video Details</h3>
          <div className="detail-item">
            <span className="detail-label">Pet Name:</span>
            <span className="detail-value">{videoJob.petName}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Breed:</span>
            <span className="detail-value">{videoJob.breed}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Emotion:</span>
            <span className="detail-value">{videoJob.emotion}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Created:</span>
            <span className="detail-value">
              {new Date(videoJob.createdAt).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoStatus;
